'use client';
import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';
import { useMedia } from 'react-use';

import { useIsMobileDevice } from './hooks/useIsMobileDevice';

export const sizes = {
  '4xlarge': 1900,
  large: 920,
  medium: 700,
  small: 560,
  xlarge: 1104,
  xsmall: 350,
  xxlarge: 1174,
  xxxlarge: 1600,
  zero: 0,
};

type Props = {
  children: ReactNode;
};

const defaultValue = {
  isAbove4XLScreen: false,
  isAboveLargeScreen: false,
  isAboveMediumScreen: true,
  isAboveSmallScreen: true,
  isAboveXLScreen: false,
  isAboveXSmallScreen: true,
  isAboveXXLScreen: false,
  isAboveXXXLScreen: false,
};

const MediaQueryContext = createContext(defaultValue);

const mediaQueries = {
  isAbove4XL: `(min-width: ${sizes['4xlarge']}px)`, // min-width: breakpoints.4xl
  isAboveLarge: `(min-width: ${sizes.large}px)`, // min-width: breakpoints.large
  isAboveMedium: `(min-width: ${sizes.medium}px)`, // min-width: breakpoints.medium
  isAboveSmall: `(min-width: ${sizes.small}px)`, // min-width: breakpoints.small
  isAboveXL: `(min-width: ${sizes.xlarge}px)`, // min-width: breakpoints.xl
  isAboveXSmall: `(min-width: ${sizes.xsmall}px)`, // min-width: breakpoints.xsmall
  isAboveXXL: `(min-width: ${sizes.xxlarge}px)`, // min-width: breakpoints.xxl
  isAboveXXXL: `(min-width: ${sizes.xxxlarge}px)`, // min-width: breakpoints.xxxl
};

/**
 * Static mobile values - created once at module load with a stable reference.
 * This prevents re-renders when the device rotates, as the same object is always returned.
 */
const MOBILE_STATIC_VALUE = {
  isAbove4XLScreen: false,
  isAboveLargeScreen: false,
  isAboveMediumScreen: false,
  isAboveSmallScreen: false,
  isAboveXLScreen: false,
  isAboveXSmallScreen: true,
  isAboveXXLScreen: false,
  isAboveXXXLScreen: false,
} as const;

export const MediaQueryProvider = memo(({ children }: Props) => {
  const { isMobileDevice } = useIsMobileDevice();

  const isAboveXSmallScreen = useMedia(mediaQueries.isAboveXSmall, defaultValue.isAboveXSmallScreen);
  const isAboveSmallScreen = useMedia(mediaQueries.isAboveSmall, defaultValue.isAboveSmallScreen);
  const isAboveMediumScreen = useMedia(mediaQueries.isAboveMedium, defaultValue.isAboveMediumScreen);
  const isAboveLargeScreen = useMedia(mediaQueries.isAboveLarge, defaultValue.isAboveLargeScreen);
  const isAboveXLScreen = useMedia(mediaQueries.isAboveXL, defaultValue.isAboveXLScreen);
  const isAboveXXLScreen = useMedia(mediaQueries.isAboveXXL, defaultValue.isAboveXXLScreen);
  const isAboveXXXLScreen = useMedia(mediaQueries.isAboveXXXL, defaultValue.isAboveXXXLScreen);
  const isAbove4XLScreen = useMedia(mediaQueries.isAbove4XL, defaultValue.isAbove4XLScreen);
  const desktopValue = useMemo(
    () => ({
      isAbove4XLScreen,
      isAboveLargeScreen,
      isAboveMediumScreen,
      isAboveSmallScreen,
      isAboveXLScreen,
      isAboveXSmallScreen,
      isAboveXXLScreen,
      isAboveXXXLScreen,
    }),
    [
      isAbove4XLScreen,
      isAboveSmallScreen,
      isAboveMediumScreen,
      isAboveLargeScreen,
      isAboveXLScreen,
      isAboveXXLScreen,
      isAboveXSmallScreen,
      isAboveXXXLScreen,
    ],
  );

  /**
   * On mobile: return stable object reference (prevents rotation re-renders)
   * On desktop: return computed values based on window size
   */
  const value = useMemo(() => (isMobileDevice ? MOBILE_STATIC_VALUE : desktopValue), [isMobileDevice, desktopValue]);

  return <MediaQueryContext.Provider value={value}>{children}</MediaQueryContext.Provider>;
});

MediaQueryProvider.displayName = 'MediaQueryProvider';

export function useBreakpointsContext() {
  const context = useContext(MediaQueryContext);

  if (context === defaultValue) {
    throw 'useBreakpointsContext used outside of MediaQueryProvider';
  }

  return context;
}
